/**
 * Final Inspection Report Service
 * Frontend API service for FIR management
 */

import apiService from './api.service';

export interface FinalInspectionReport {
  id: string;
  fir_number: string;
  work_order_id: string;
  work_order_number: string;
  item_name: string;
  inspection_date: string;
  inspector_name: string;
  /** QMS Knex report status, or FIR request operational status when `_recordSource` is `fir_request`. */
  status: 'Pending' | 'Approved' | 'Rejected' | 'On Hold' | 'In Progress' | 'Partial';
  approved_quantity: number;
  rejected_quantity: number;
  total_quantity: number;
  approval_status: 'Pending Approval' | 'Approved' | 'Rejected';
  approved_by?: string;
  approved_at?: string;
  rejection_reason?: string;
  attachments?: Attachment[];
  quality_parameters?: QualityParameter[];
  inspection_criteria?: string | null;
  inspection_equipment?: FirInspectionEquipment[];
  remarks?: string;
  created_at: string;
  /** Knex QMS document vs TypeORM per-line FIR from work order completion */
  _recordSource?: 'qms_report' | 'fir_request';
}

export interface Attachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  uploaded_by: string;
  uploaded_at: string;
  file_url?: string;
  url?: string;
}

export interface QualityParameter {
  id: string;
  parameter_name: string;
  specification: string;
  measured_value: string;
  result: 'Pass' | 'Fail';
  remarks?: string;
}

export interface FirInspectionEquipment {
  asset_id: string;
  asset_code: string;
  asset_name: string;
  sort_order: number;
}

export interface UpdateFirInspectionDetailsRequest {
  inspection_criteria?: string | null;
  equipment_asset_ids?: string[];
  tmmde_override_reason?: string;
}

export interface CreateFIRRequest {
  work_order_id: string;
  inspection_date: string;
  approved_quantity: number;
  rejected_quantity: number;
  rejection_reasons?: string;
  quality_parameters?: Omit<QualityParameter, 'id'>[];
  remarks?: string;
}

export interface ApproveFIRRequest {
  approval_status: 'Approved' | 'Rejected';
  approval_comments?: string;
  approval_documents?: string[];
  rejection_reason?: string;
  approved_quantity?: number;
  rejected_quantity?: number;
}

class FinalInspectionReportService {
  private readonly baseUrl = '/quality/final-inspection-reports';

  /**
   * Get all Final Inspection Reports
   */
  async getAll(filters?: {
    status?: string;
    approval_status?: string;
    work_order_id?: string;
    from_date?: string;
    to_date?: string;
  }): Promise<FinalInspectionReport[]> {
    const response = await apiService.get(this.baseUrl, { params: filters });
    return response.data;
  }

  /**
   * Get Report by ID
   */
  async getById(id: string): Promise<FinalInspectionReport> {
    const response = await apiService.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /**
   * Create Final Inspection Report
   */
  async create(data: CreateFIRRequest): Promise<FinalInspectionReport> {
    const response = await apiService.post(this.baseUrl, data);
    return response.data;
  }

  /**
   * Approve or Reject Final Inspection Report
   */
  async approve(id: string, data: ApproveFIRRequest): Promise<FinalInspectionReport> {
    const response = await apiService.post(`${this.baseUrl}/${id}/approve`, data);
    return response.data;
  }

  /**
   * Add Attachment
   */
  async addAttachment(id: string, file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.post(`${this.baseUrl}/${id}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  /**
   * Delete Attachment
   */
  async deleteAttachment(attachmentId: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/attachments/${attachmentId}`);
  }

  /**
   * Get Reports by Work Order
   */
  async getByWorkOrder(workOrderId: string): Promise<FinalInspectionReport[]> {
    const response = await apiService.get(`${this.baseUrl}/work-order/${workOrderId}`);
    return response.data;
  }

  /**
   * Get Dashboard Statistics
   */
  async getDashboardStats(): Promise<any> {
    const response = await apiService.get(`${this.baseUrl}/dashboard/stats`);
    return response.data;
  }

  async updateInspectionDetails(
    id: string,
    data: UpdateFirInspectionDetailsRequest
  ): Promise<FinalInspectionReport> {
    const response = await apiService.put(`${this.baseUrl}/${id}/inspection-details`, data);
    return response.data;
  }
}

export const finalInspectionReportService = new FinalInspectionReportService();
